import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAiAccess } from '../ai/useAiAccess';
import { useT } from '../i18n/i18n';
import './testers.css';

type Tester = { userId: string; email: string | null; name: string | null; monthlyCredits: number; used: number; left: number; extra: number };

function Row({ tester, onSaved }: { tester: Tester; onSaved: (t: Tester) => void }) {
  const t = useT();
  const [value, setValue] = useState(String(tester.monthlyCredits));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 100000;
  const changed = valid && n !== tester.monthlyCredits;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await api<Tester>(`/ai/testers/${tester.userId}`, { method: 'PUT', token: true, body: { monthlyCredits: n } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        {tester.email ?? t('Has not signed in to EasyLearning yet')}
        {tester.name && <div className="muted">{tester.name}</div>}
        {error && <div className="error" role="alert">{error}</div>}
      </td>
      <td>
        <div className="tester-credits">
          <label className="visually-hidden" htmlFor={`allow-${tester.userId}`}>{t('Monthly AI credits for {who}', { who: tester.email ?? tester.userId })}</label>
          <input id={`allow-${tester.userId}`} type="number" inputMode="numeric" min={0} max={100000} step={1} value={value}
            onChange={(e) => setValue(e.target.value)} style={{ maxWidth: '8rem' }} />
          <button type="button" className="btn secondary" disabled={!changed || busy} onClick={() => void save()}>
            {busy ? t('Saving…') : t('Save')}
          </button>
        </div>
      </td>
      <td className="num">{tester.monthlyCredits > 0 ? t('{used} used · {left} left', { used: tester.used, left: tester.left }) : t('No AI')}</td>
    </tr>
  );
}

export default function TestersPage() {
  const t = useT();
  const { access } = useAiAccess();
  const [testers, setTesters] = useState<Tester[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<Tester[]>('/ai/testers', { token: true })
      .then(setTesters)
      .catch((e) => setError(e instanceof Error ? e.message : t('Could not load.')));
  }, []);
  useEffect(() => { if (access?.developer) load(); }, [access?.developer, load]);

  if (!access) return <p className="muted">{t('Loading…')}</p>;
  if (!access.developer) return <div className="card"><p>{t('This page is for developers.')}</p></div>;

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{t('Testers and their AI credits')}</h2>
      <p>{t('Testers use EasyLearning without paying. Add or remove them in the testers group on the Fractalon admin page. EasyLearning AI is not free for them: each gets the monthly credits you set here, refilled every month. 0 means no AI.')}</p>
      {error && <p className="error" role="alert">{error}</p>}
      {testers === null && !error && <p className="muted">{t('Loading…')}</p>}
      {testers?.length === 0 && <p className="muted">{t('Nobody is in the testers group yet.')}</p>}
      {testers && testers.length > 0 && (
        <div className="table-wrap">
          <table className="report-table testers-table">
            <thead>
              <tr>
                <th scope="col">{t('Tester')}</th>
                <th scope="col">{t('Credits a month')}</th>
                <th scope="col" className="num">{t('This month')}</th>
              </tr>
            </thead>
            <tbody>
              {testers.map((x) => (
                <Row key={x.userId} tester={x} onSaved={(fresh) => setTesters((cur) => cur?.map((c) => (c.userId === fresh.userId ? fresh : c)) ?? null)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
